import t from "@/lang/lang";
import {
  Pest,
  PestGroup,
  PestGroupSnapshot,
  PestKind,
} from "@/services/backend/pestcontrol/types/pest";
import { Filter } from "@/shared/lib/filter/filter";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import { v4 } from "uuid";

export type PestGroupOrderBy = "name" | "description";
export type PestOrderBy = "kind" | "name" | "group" | "technicalTerm";
export type PestGroupFilterValue = string | "all" | "none";

export function newGroupDraft(companyId: string): PestGroup {
  return {
    id: v4(),
    companyId,
    name: "",
    description: "",
  };
}

export function newPestDraft(
  companyId: string,
  kind: PestKind,
  group?: PestGroup,
): Pest {
  return {
    id: v4(),
    companyId,
    kind,
    group: group ? groupToSnapshot(group) : undefined,
    name: "",
    technicalTerm: "",
    abbreviation: "",
    description: "",
    synonyms: [],
  };
}

export function groupToSnapshot(
  group: PestGroup | undefined,
): PestGroupSnapshot | undefined {
  if (!group) {
    return undefined;
  }
  return {
    companyId: group.companyId,
    sourceId: group.id,
    name: group.name,
    description: group.description ?? "",
    takenAt: new Date().toISOString(),
  };
}

export function parseSynonyms(value: string): string[] {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

export function formatSynonyms(values: string[] | undefined): string {
  if (!values || values.length === 0) return "-";
  return values.join(", ");
}

export function normalizePest(pest: Pest): Pest {
  return {
    ...pest,
    synonyms: Array.isArray(pest.synonyms) ? pest.synonyms : [],
  };
}

export function filterGroups(
  groups: PestGroup[],
  search: Filter<string>,
): PestGroup[] {
  if (!search.active || !search.values?.[0]) {
    return groups;
  }
  const term = search.values[0].toLowerCase();
  return groups.filter((group) =>
    [group.name, group.description ?? ""].some((value) =>
      value.toLowerCase().includes(term),
    ),
  );
}

export function orderGroups(
  groups: PestGroup[],
  orderBy: OrdrBy<PestGroupOrderBy>[],
): PestGroup[] {
  return [...groups].sort((left, right) =>
    compareOrdered(left, right, orderBy, groupOrderValue),
  );
}

export function orderPests(
  pests: Pest[],
  orderBy: OrdrBy<PestOrderBy>[],
): Pest[] {
  return [...pests].sort((left, right) =>
    compareOrdered(left, right, orderBy, pestOrderValue),
  );
}

export function filterPests(
  pests: Pest[],
  groupFilter: PestGroupFilterValue,
): Pest[] {
  if (groupFilter === "all") {
    return pests;
  }
  if (groupFilter === "none") {
    return pests.filter((pest) => !pest.group);
  }
  return pests.filter((pest) => pest.group?.sourceId === groupFilter);
}

function compareOrdered<T, TField extends string>(
  left: T,
  right: T,
  orderBy: OrdrBy<TField>[],
  value: (item: T, field: TField) => string,
): number {
  const fields = orderBy.length > 0 ? orderBy : [{ field: "name" as TField }];
  for (const order of fields) {
    const result = value(left, order.field).localeCompare(
      value(right, order.field),
      "de",
      { sensitivity: "base" },
    );
    if (result !== 0) {
      return order.dsc ? -result : result;
    }
  }
  return 0;
}

function groupOrderValue(group: PestGroup, field: PestGroupOrderBy): string {
  switch (field) {
    case "description":
      return group.description ?? "";
    case "name":
    default:
      return group.name;
  }
}

function pestOrderValue(pest: Pest, field: PestOrderBy): string {
  switch (field) {
    case "kind":
      return pest.kind;
    case "group":
      return pest.group?.name ?? "";
    case "technicalTerm":
      return pest.technicalTerm ?? "";
    case "name":
    default:
      return pest.name;
  }
}

export function formatPestKind(kind: PestKind): string {
  switch (kind) {
    case PestKind.Category:
      return t("Kategorie");
    case PestKind.Indicator:
      return t("Indikator");
    default:
      return kind;
  }
}
